import * as React from 'react';

/**
 * React context to store the width expanded state of the side-panel.
 */
export const SidePanelExpandableContext = React.createContext<{
    isExpanded: boolean;
    setExpanded: React.Dispatch<React.SetStateAction<boolean>>;
}>({
    isExpanded: false,
    setExpanded: () => {},
});

/**
 * A column the layout keeps aside for the side-panel.
 */
export type SidePanelHost = {
    /**
     * Where to render the panel, or null for as long as the layout is still attaching it.
     *
     * A layout knows that it will host the panel one render before it knows where: the element
     * exists only once it has been committed. Offering the column without it lets the panel wait
     * out that render instead of falling back over the page, so it is mounted once — in the place
     * it is going to stay — rather than built, torn down and built again somewhere else.
     */
    element: HTMLElement | null;
};

/**
 * Column the layout offers for the side-panel to render into.
 *
 * `null` — the default — leaves the panel overlaying its own page, which is how a page with a
 * side-panel stands on its own. A layout that already keeps a column aside for it offers that
 * column instead, and the panel renders there: the page then keeps all of its width, and the
 * column decides what else shares it.
 *
 * Taking the column away again moves the panel back over the page, and React rebuilds it on the
 * way: a panel's own state does not survive the move, and its effects run again. So a layout should
 * offer or withdraw a column when the shape of the window changes, not while the user works, and a
 * panel with anything worth keeping — a half-written comment, say — should keep it somewhere that
 * outlives the panel.
 */
export const SidePanelHostContext = React.createContext<SidePanelHost | null>(null);
